import { Component } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { BaseModalComponent } from '@app/components/base-modal/base-modal.component';
import { AbstractControl, FormControl, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { IncidentsService } from '@app/services/incidents/incidents.service';
import { IncidentDetailsPageStore } from '../incident-details-page.store';
import { catchError, firstValueFrom, of } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { AddDuplicateResultModel } from '../merge-view/models/add-duplicate-result.model';

@Component({
  standalone: true,
  selector: 'app-add-duplicate-modal',
  imports: [
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    BaseModalComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './add-duplicate-modal.component.html',
  styleUrl: './add-duplicate-modal.component.scss',
})
export class AddDuplicateModalComponent {
  private readonly incidentUrlPattern = /^https:\/\/raven(?:\.(?:dev|qa))?\.munichre\.com\/incidents\/(\d+)\/?$/i;
  private readonly incidentIdPattern = /^\d+$/;
  private readonly incidentIdOrUrlValidator = (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const value = String(control.value).trim();

    // Accept either a numeric incident ID or a valid Raven incident URL
    return this.incidentIdPattern.test(value) || this.incidentUrlPattern.test(value)
      ? null
      : { invalidIncidentIdOrUrl: true };
  };

  protected incidentControl = new FormControl('', [Validators.required, this.incidentIdOrUrlValidator]);

  constructor(
    private readonly dialogRef: MatDialogRef<AddDuplicateModalComponent>,
    private readonly incidentService: IncidentsService,
    private readonly incidentDetailsPageStore: IncidentDetailsPageStore,
  ) {
    this.dialogRef.disableClose = true;
  }

  protected async onAddDuplicateAsync(): Promise<void> {
    if (this.incidentControl.invalid) {
      this.incidentControl.markAsTouched();
      return;
    }

    this.clearBackendControlError();

    const incidentIdOrUrl = String(this.incidentControl.value ?? '').trim();
    const incidentUrlMatch = this.incidentUrlPattern.exec(incidentIdOrUrl);

    const duplicateCandidateIncidentId = this.incidentIdPattern.test(incidentIdOrUrl)
      ? incidentIdOrUrl
      : incidentUrlMatch?.[1];
    if (!duplicateCandidateIncidentId) {
      return;
    }

    const updatedMasterIncident = await firstValueFrom(
      this.incidentService
        .addDuplicateIncident(this.incidentDetailsPageStore.incident()!.id, Number(duplicateCandidateIncidentId))
        .pipe(
          catchError((response: HttpErrorResponse) => {
            this.incidentControl.setErrors({
              ...(this.incidentControl.errors ?? {}),
              backendError: String(response.error.detail ?? ''),
            });
            this.incidentControl.markAsTouched();

            return of(null);
          }),
        ),
    );

    if (updatedMasterIncident) {
      this.dialogRef.close({
        addedIncidentId: duplicateCandidateIncidentId,
        updatedIncident: updatedMasterIncident,
      } as AddDuplicateResultModel);
    }
  }

  protected onCancel(): void {
    this.dialogRef.close();
  }

  private clearBackendControlError(): void {
    const controlErrors = this.incidentControl.errors;
    if (!controlErrors || !('backendError' in controlErrors)) {
      return;
    }

    const remainingErrors = { ...controlErrors };
    delete remainingErrors['backendError'];
    this.incidentControl.setErrors(Object.keys(remainingErrors).length ? remainingErrors : null);
  }
}
